import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime, formatGel, formatPoints } from '../lib/format';
import { useCodeLabel, useI18n } from '../lib/i18n';
import { useOrderList } from '../lib/queries';
import { useDebounced } from '../lib/useDebounced';
import { Amount, EmptyState, Pill, Skeleton, TierBadge } from '../components/ui';

// The stages a car passes through live on the board; this list is the ledger.
const STATUSES = ['COMPLETED', 'PARTIALLY_REFUNDED', 'REFUNDED', 'CANCELLED'] as const;

const STATUS_TONE: Record<string, 'neutral' | 'warning' | 'danger'> = {
  PARTIALLY_REFUNDED: 'warning',
  REFUNDED: 'warning',
  CANCELLED: 'danger',
};

export function OrdersPage() {
  const { t } = useI18n();
  const code = useCodeLabel();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const debounced = useDebounced(search);
  const { data, isPending, isFetching } = useOrderList(debounced, status);

  const items = data?.items ?? [];

  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-5">
        <h1 className="font-display text-[1.75rem] font-bold leading-tight tracking-tight text-ink sm:text-[2rem]">
          {t('orders.title')}
        </h1>
      </header>

      <div className="relative mb-3">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('orders.searchPlaceholder')}
          aria-label={t('orders.search')}
          className="w-full rounded-xl border border-line bg-raised px-4 py-3 text-ink transition-colors placeholder:text-ink-faint focus:border-brand focus:bg-muted focus:outline-none"
        />
        {isFetching && !isPending && (
          <span className="absolute right-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin rounded-full border-2 border-brand border-t-transparent" />
        )}
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5">
        <button
          onClick={() => setStatus(undefined)}
          aria-pressed={!status}
          className={`inline-flex min-h-10 items-center rounded-lg border px-3 text-xs font-medium transition-colors ${
            !status ? 'border-brand bg-brand-wash text-brand' : 'border-line bg-raised text-ink-dim'
          }`}
        >
          {t('orders.filterAll')}
        </button>
        {STATUSES.map((option) => (
          <button
            key={option}
            onClick={() => setStatus(status === option ? undefined : option)}
            aria-pressed={status === option}
            className={`inline-flex min-h-10 items-center rounded-lg border px-3 text-xs font-medium transition-colors ${
              status === option
                ? 'border-brand bg-brand-wash text-brand'
                : 'border-line bg-raised text-ink-dim hover:text-ink'
            }`}
          >
            {code('status', option)}
          </button>
        ))}
      </div>

      {isPending && (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-[4.5rem]" />)}</div>
      )}

      {!isPending && items.length === 0 && (
        <EmptyState
          title={debounced ? t('orders.noMatch', { query: debounced }) : t('orders.empty')}
        />
      )}

      <div className="space-y-2">
        {items.map((order) => (
          <Link
            key={order.id}
            to={`/orders/${order.id}`}
            className="group block rounded-2xl border border-line bg-surface px-4 py-3.5 transition-colors hover:border-brand/40 hover:bg-raised sm:px-5"
          >
            <div className="sm:flex sm:items-center sm:gap-6">
              <div className="min-w-0 sm:flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="tabular text-sm font-semibold text-ink transition-colors group-hover:text-brand">
                    {order.reference}
                  </span>
                  {order.tierCode && <TierBadge code={order.tierCode} size="sm" />}
                  {STATUS_TONE[order.status] && (
                    <Pill tone={STATUS_TONE[order.status]}>{code('status', order.status)}</Pill>
                  )}
                  {order.isLarge && <Pill tone="warning">{t('orders.large')}</Pill>}
                  {order.outstandingTetri > 0 && (
                    <Pill tone="danger">
                      {t('payment.unpaid', { amount: formatGel(order.outstandingTetri) })}
                    </Pill>
                  )}
                </div>
                <div className="mt-1 truncate text-xs text-ink-faint">
                  {order.customerName}
                  <span className="mx-1.5 opacity-50">·</span>
                  {order.completedAt ? formatDateTime(order.completedAt) : t('order.notCompleted')}
                </div>
              </div>

              <div className="mt-3 flex items-center gap-5 border-t border-line/60 pt-2.5 sm:mt-0 sm:border-0 sm:pt-0">
                <div className="sm:text-right">
                  <Amount
                    value={formatGel(order.orderValueTetri)}
                    className="block text-sm font-semibold text-ink"
                  />
                  <div className="text-[0.62rem] uppercase tracking-wider text-ink-faint">{t('orders.value')}</div>
                </div>
                {/* Null for anyone not allowed to see pay — the server leaves it out. */}
                {order.staffPayTetri !== null && order.staffPayTetri > 0 && (
                  <div className="sm:text-right">
                    <Amount
                      value={formatGel(order.staffPayTetri)}
                      className="block text-sm font-semibold text-ink-dim"
                    />
                    <div className="text-[0.62rem] uppercase tracking-wider text-ink-faint">{t('pay.column')}</div>
                  </div>
                )}
                <div className="sm:text-right">
                  <div className="tabular text-sm font-semibold text-positive">
                    +{formatGel(order.cashbackEarnedTetri)}
                  </div>
                  <div className="text-[0.62rem] uppercase tracking-wider text-ink-faint">{t('orders.earned')}</div>
                </div>
                <div className="tabular hidden sm:block sm:text-right">
                  <div className="text-sm font-semibold text-ink">
                    +{formatPoints(order.pointsEarned)}
                  </div>
                  <div className="text-[0.62rem] uppercase tracking-wider text-ink-faint">{t('common.points')}</div>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
